import { FC, useCallback, useMemo, useState } from 'react';

import { ErdRelationship } from '../types';

interface ErdReviewPanelProps {
  relationship: ErdRelationship;
  sourceModelName: string;
  targetModelName: string;
  onApprove: (relationshipId: string) => void;
  onDismiss: (relationshipId: string) => void;
  onClose: () => void;
}

function generateYamlSnippet(
  sourceModelName: string,
  targetModelName: string,
  sourceColumn: string,
  targetColumn: string,
): string {
  return `  - name: ${sourceModelName}
    columns:
      - name: ${sourceColumn}
        tests:
          - relationships:
              to: ref('${targetModelName}')
              field: ${targetColumn}`;
}

export const ErdReviewPanel: FC<ErdReviewPanelProps> = ({
  relationship,
  sourceModelName,
  targetModelName,
  onApprove,
  onDismiss,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);

  const yamlSnippet = useMemo(
    () =>
      generateYamlSnippet(
        sourceModelName,
        targetModelName,
        relationship.sourceColumn,
        relationship.targetColumn,
      ),
    [sourceModelName, targetModelName, relationship],
  );

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(yamlSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard API
    }
  }, [yamlSnippet]);

  const isApproved = relationship.approvalStatus === 'approved';

  return (
    <div
      className="flex h-full w-80 flex-col border-l border-borderMuted bg-bgMain"
      data-testid="erd-review-panel"
    >
      <div className="flex items-center justify-between border-b border-borderMuted p-4">
        <h3 className="text-sm font-semibold text-fgMain">Review Relationship</h3>
        <button className="text-fgDecorative hover:text-fgMain" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="mb-4">
          <div className="mb-1 text-xs font-semibold text-fgDecorative">Connection</div>
          <div className="rounded border border-borderMuted p-2">
            <div className="text-sm font-medium text-fgMain">
              {sourceModelName}
              <span className="text-fgDecorative"> → </span>
              {targetModelName}
            </div>
            <div className="mt-1 text-xs text-fgDecorative">
              {relationship.sourceColumn} → {relationship.targetColumn}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="mb-1 text-xs font-semibold text-fgDecorative">Confidence</div>
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              relationship.confidence === 'high'
                ? 'bg-bgSuccessMuted text-fgSuccess'
                : 'bg-bgWarningMuted text-fgWarning'
            }`}
          >
            {relationship.confidence === 'high' ? 'High' : 'Medium'}
          </span>
        </div>

        <div className="mb-4">
          <div className="mb-1 text-xs font-semibold text-fgDecorative">Evidence</div>
          <ul className="space-y-1 text-xs text-fgMain">
            <li className="flex items-start gap-1.5">
              <span className="mt-0.5 text-fgSuccess">&#10003;</span>
              Column name pattern:{' '}
              <code className="text-fgBrand">{relationship.sourceColumn}</code> matches
              model <code className="text-fgBrand">{targetModelName}</code>
            </li>
            {relationship.hasUniqueTest && (
              <li className="flex items-start gap-1.5">
                <span className="mt-0.5 text-fgSuccess">&#10003;</span>
                Target model has a unique test on the matching column
              </li>
            )}
            {!relationship.hasUniqueTest && (
              <li className="flex items-start gap-1.5">
                <span className="mt-0.5 text-fgWarning">&#8226;</span>
                No unique test found on target column
              </li>
            )}
          </ul>
        </div>

        <div className="mb-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-fgDecorative">
              Generated YAML
            </span>
            <button
              className="text-xs text-fgBrand hover:opacity-80"
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="text-[10px] leading-relaxed text-fgDecorative">
            Copy YAML to add this relationship test to your schema.yml
          </div>
          <pre className="mt-1 overflow-x-auto rounded bg-bgNeutralMuted p-2 text-[11px] text-fgMain">
            {yamlSnippet}
          </pre>
        </div>
      </div>

      <div className="flex gap-2 border-t border-borderMuted p-3">
        {!isApproved ? (
          <>
            <button
              className="flex-1 rounded-md bg-bgSuccessMuted px-3 py-1.5 text-sm font-medium text-fgSuccess transition-colors hover:opacity-80"
              onClick={() => onApprove(relationship.id)}
              data-testid="erd-approve-relationship"
            >
              Approve
            </button>
            <button
              className="flex-1 rounded-md bg-bgDangerMuted px-3 py-1.5 text-sm font-medium text-fgDanger transition-colors hover:opacity-80"
              onClick={() => onDismiss(relationship.id)}
              data-testid="erd-dismiss-relationship"
            >
              Dismiss
            </button>
          </>
        ) : (
          <div className="flex-1 rounded-md bg-bgSuccessMuted px-3 py-1.5 text-center text-sm font-medium text-fgSuccess">
            Approved
          </div>
        )}
      </div>
    </div>
  );
};
